import { useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface UserRole {
  role: string;
}

export interface UserSubscription {
  plan: 'basic' | 'standard';
  status: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isPtaAdmin, setIsPtaAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserData(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Use setTimeout to avoid calling Supabase in the callback
        setTimeout(() => {
          fetchUserData(session.user.id);
        }, 0);
      } else {
        setRoles([]);
        setSubscription(null);
        setIsPtaAdmin(false);
        setLoading(false);
      }
    });

    return () => authSubscription.unsubscribe();
  }, []);

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesError) throw rolesError;
      setRoles(rolesData?.map(r => r.role) || []);

      // Fetch user's PTA
      const { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('pta_id, is_pta_admin')
        .eq('user_id', userId)
        .maybeSingle();

      if (memberError) throw memberError;
      setIsPtaAdmin(memberData?.is_pta_admin || false);

      // Fetch PTA's subscription (not user's subscription)
      if (memberData?.pta_id) {
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('plan, status, expires_at')
          .eq('pta_id', memberData.pta_id)
          .in('status', ['active', 'trial'])
          .maybeSingle();

        if (subError) throw subError;

        // Check if subscription has expired
        if (subData?.expires_at && new Date(subData.expires_at) < new Date()) {
          // Mark as expired
          await supabase
            .from('subscriptions')
            .update({ status: 'expired' })
            .eq('pta_id', memberData.pta_id)
            .eq('status', 'active');
          
          setSubscription(null);
        } else {
          setSubscription(subData);
        }
      } else {
        setSubscription(null);
      }

    } catch (error: any) {
      console.error('Error fetching user data:', error);
      toast({
        title: "Error",
        description: "Failed to load user data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/pta-admin`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        }
      }
    });
    
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const hasRole = (role: string) => roles.includes(role);
  const hasPlan = (plan: 'basic' | 'standard') => subscription?.plan === plan || (plan === 'basic' && subscription?.plan === 'standard');

  return {
    user,
    session,
    roles,
    subscription,
    isPtaAdmin,
    loading,
    signIn,
    signUp,
    signOut,
    hasRole,
    hasPlan,
  };
};
